import { useCallback, useState, type MouseEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { SkipOnboardingDialog } from "../components/SkipOnboardingDialog";

/** The in-app path a plain left click on a link would open, else null. */
const interceptedPath = (e: MouseEvent): string | null => {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
    return null;
  const link = (e.target as Element | null)?.closest?.("a[href]");
  if (!link || link.getAttribute("target") === "_blank") return null;
  const href = link.getAttribute("href") ?? "";
  return href.startsWith("/") ? href : null;
};

/**
 * While `active`, a click on an in-app link inside the guarded element asks
 * the member to confirm skipping setup before it navigates. Links opened in
 * a new tab leave onboarding open, so they pass through.
 */
export const useSkipOnboardingGuard = (
  active: boolean
): { onClickCapture?: (e: MouseEvent) => void; dialog: ReactNode } => {
  const navigate = useNavigate();
  const [to, setTo] = useState<string | null>(null);
  const onClickCapture = useCallback((e: MouseEvent) => {
    const path = interceptedPath(e);
    if (!path) return;
    e.preventDefault();
    e.stopPropagation();
    setTo(path);
  }, []);
  const dialog = active ? (
    <SkipOnboardingDialog
      to={to}
      onCancel={() => setTo(null)}
      onSkip={(path) => {
        setTo(null);
        navigate(path);
      }}
    />
  ) : null;
  return { onClickCapture: active ? onClickCapture : undefined, dialog };
};
